import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { display } from '../../config/config';
import { Currency } from '../../types/currency';
import { Icon } from '../icons/Icon';
import styles from './AmountInput.module.less';

interface AmountInputProps {
  currency: Currency;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  errorKey?: number;
  autoFocus?: boolean;
  onSubmit?: () => void;
}

const MAX_FIAT_DISPLAY_DIGITS = display.maxFiatDigits;

const formatDisplayValue = (value: string, locale: string): string => {
  if (!value) {
    return '';
  }

  try {
    const num = parseFloat(value.replace(/,/g, ''));
    if (isNaN(num)) {
      return value;
    }

    const rounded = Math.round(num);
    const formatted = rounded.toLocaleString(locale);

    // Truncate fiat display if too many digits (excluding group separators)
    const digitCount = formatted.replace(/\D/g, '').length;
    if (digitCount > MAX_FIAT_DISPLAY_DIGITS) {
      // Show first MAX_FIAT_DISPLAY_DIGITS digits
      const digits = formatted.replace(/\D/g, '');
      const truncatedDigits = digits.substring(0, MAX_FIAT_DISPLAY_DIGITS);
      return parseInt(truncatedDigits).toLocaleString(locale);
    }

    return formatted;
  } catch {
    return value;
  }
};

export const AmountInput: React.FC<AmountInputProps> = ({
  currency,
  value,
  onChange,
  error,
  errorKey = 0,
  autoFocus = false,
  onSubmit,
}) => {
  const intl = useIntl();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [inputWidth, setInputWidth] = useState('1ch');
  const hasShownErrorRef = useRef(false);
  const isAutoFocusingRef = useRef(false);

  // Track if error has ever been shown
  useEffect(() => {
    if (error) {
      hasShownErrorRef.current = true;
    } else {
      hasShownErrorRef.current = false;
    }
  }, [error]);

  useEffect(() => {
    if (!autoFocus || !inputRef.current) return;
    const input = inputRef.current;
    isAutoFocusingRef.current = true;
    input.focus();
    const len = input.value.length;
    input.setSelectionRange(len, len);
  }, [autoFocus]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (isAutoFocusingRef.current) {
      isAutoFocusingRef.current = false;
      const len = e.currentTarget.value.length;
      e.currentTarget.setSelectionRange(len, len);
      return;
    }
    e.currentTarget.select();
  };

  // Scroll into view when error appears
  useEffect(() => {
    if (error && containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [errorKey, error]);

  // Measure text width for dynamic input sizing using Canvas
  useLayoutEffect(() => {
    const measureWidth = () => {
      const displayValue = formatDisplayValue(value, intl.locale) || '0';

      if (!canvasRef.current) {
        canvasRef.current = document.createElement('canvas');
      }

      const context = canvasRef.current.getContext('2d');
      if (context && inputRef.current) {
        // Get computed styles from the actual input element
        const computedStyle = window.getComputedStyle(inputRef.current);
        const fontWeight = computedStyle.fontWeight;
        const fontSize = computedStyle.fontSize;
        const fontFamily = computedStyle.fontFamily;
        const letterSpacing = parseFloat(computedStyle.letterSpacing) || 0;

        // Set canvas font to match input's computed style
        context.font = `${fontWeight} ${fontSize} ${fontFamily}`;
        const metrics = context.measureText(displayValue);

        // Account for letter-spacing manually: letterSpacing per character (except last)
        const letterSpacingAdjustment =
          Math.max(0, displayValue.length - 1) * letterSpacing;
        const width = metrics.width + letterSpacingAdjustment;

        setInputWidth(`${Math.max(width + 4, 20)}px`);
      }
    };

    measureWidth();

    // Recalculate when fonts finish loading
    document.fonts.ready.then(measureWidth);
  }, [value, intl.locale]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSubmit?.();
      return;
    }

    // Allow control keys and Ctrl/Cmd combinations
    if (
      e.key.length > 1 ||
      e.ctrlKey ||
      e.metaKey
    ) {
      return;
    }

    // Block non-digit keys
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const inputValue = input.value.replace(/\D/g, '');

    // Allow empty or valid numbers
    if (inputValue === '' || /^\d*$/.test(inputValue)) {
      // Enforce max digits limit
      if (inputValue.length <= MAX_FIAT_DISPLAY_DIGITS) {
        onChange(inputValue);
        return;
      }
    }
    // Reset input to current valid value if validation fails
    input.value = formatDisplayValue(value, intl.locale);
  };

  return (
    <div
      ref={containerRef}
      className={styles.amountSection}
      onClick={() => inputRef.current?.focus()}
    >
      <div className={styles.amountDisplay}>
        <span className={styles.currencySymbol}>{currency.toUpperCase()}</span>
        <input
          ref={inputRef}
          className={styles.amountInput}
          type="text"
          value={formatDisplayValue(value, intl.locale)}
          onKeyDown={handleKeyDown}
          onInput={handleChange}
          onFocus={handleFocus}
          placeholder="0"
          autoComplete="off"
          inputMode="numeric"
          style={{ width: inputWidth }}
        />
      </div>
      {error && (
        <div
          className={`${styles.errorMessage} ${
            !hasShownErrorRef.current
              ? styles.errorMessageFirstAppearance
              : styles.errorMessageRetrigger
          }`}
        >
          <div key={errorKey} className={styles.errorContent}>
            <Icon type="errorUp" className={styles.errorIcon} />
            <span className={styles.errorText}>{error}</span>
          </div>
        </div>
      )}
    </div>
  );
};
